<div class="leaves-container p-6 bg-gray-50 min-h-screen">
  <!-- Page Header: Premium Emerald-Teal Gradient matching the style pattern -->
  <div class="bg-gradient-to-r from-emerald-600 to-teal-800 text-white p-6 sm:p-8 rounded-xl shadow-md mb-8 transition-all duration-300">
    <h1 class="text-2xl sm:text-3xl font-bold tracking-tight">Agent Leaves Management</h1>
    <p class="text-emerald-100 text-sm sm:text-base mt-2 opacity-90">Review leaves, override configurations, and trigger penalty calculations</p>
  </div>

  <!-- Filters & Settings -->
  <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
    <h2 class="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
      <i class="fas fa-filter text-emerald-600"></i> Filter Requests
    </h2>
    <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
      <div>
        <label class="block text-sm font-semibold text-gray-700 mb-1.5">Filter by Agent</label>
        <select [(ngModel)]="selectedAgentId" (change)="onFilterChange()" class="w-full border-2 border-gray-100 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-50 rounded-lg p-2.5 bg-white text-gray-700 focus:outline-none transition-all duration-200">
          <option value="">All Agents</option>
          <option *ngFor="let agent of agents" [value]="agent.agentId">{{ agent.name }} ({{ agent.agentId }})</option>
        </select>
      </div>
      <div>
        <label class="block text-sm font-semibold text-gray-700 mb-1.5">Filter by Status</label>
        <select [(ngModel)]="statusFilter" (change)="onFilterChange()" class="w-full border-2 border-gray-100 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-50 rounded-lg p-2.5 bg-white text-gray-700 focus:outline-none transition-all duration-200">
          <option value="">All Statuses</option>
          <option value="0">Pending</option>
          <option value="1">Approved</option>
          <option value="2">Rejected</option>
          <option value="3">Unplanned</option>
        </select>
      </div>
      <div>
        <label class="block text-sm font-semibold text-gray-700 mb-1.5">Start Date (Created At)</label>
        <input type="date" [(ngModel)]="startDateFilter" (change)="onFilterChange()" class="w-full border-2 border-gray-100 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-50 rounded-lg p-2.5 text-gray-700 bg-white focus:outline-none transition-all duration-200" />
      </div>
      <div>
        <label class="block text-sm font-semibold text-gray-700 mb-1.5">End Date (Created At)</label>
        <input type="date" [(ngModel)]="endDateFilter" (change)="onFilterChange()" class="w-full border-2 border-gray-100 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-50 rounded-lg p-2.5 text-gray-700 bg-white focus:outline-none transition-all duration-200" />
      </div>
    </div>
  </div>

  <!-- Leaves Header (Desktop & Mobile Unified Header) -->
  <div class="flex items-center justify-between mb-4">
    <h3 class="font-bold text-gray-800 text-lg">Leave Requests</h3>
    <span class="text-xs font-semibold px-2.5 py-1 bg-emerald-100 text-emerald-800 rounded-full">{{ leaves.length }} total</span>
  </div>

  <!-- Desktop Table View -->
  <div class="hidden md:block bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
    <div class="overflow-x-auto">
      <table class="w-full text-left border-collapse">
        <thead>
          <tr class="bg-gray-50 border-b border-gray-150 text-gray-600 text-xs font-semibold uppercase tracking-wider">
            <th class="p-4">Agent</th>
            <th class="p-4">Requested At</th>
            <th class="p-4">Date</th>
            <th class="p-4">Quota Deducted</th>
            <th class="p-4">Reason</th>
            <th class="p-4">Status</th>
            <th class="p-4">Admin Comment</th>
            <th class="p-4 text-right">Actions</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-200 text-sm text-gray-700">
          <tr *ngFor="let leave of leaves" class="hover:bg-gray-50/80 transition-colors">
            <td class="p-4 font-medium">
              <div class="text-gray-900 font-semibold">{{ leave.agentName || 'N/A' }}</div>
              <div class="text-gray-500 text-xs mt-0.5">{{ leave.agentId }}</div>
            </td>
            <td class="p-4 text-gray-600">
              <div class="font-medium text-gray-900">{{ leave.createdAt | date:'mediumDate' }}</div>
              <div class="text-gray-500 text-xs mt-0.5">{{ leave.createdAt | date:'shortTime' }}</div>
            </td>
            <td class="p-4">
              <div class="text-gray-900 font-bold">{{ leave.date | date:'mediumDate' }}</div>
              <span class="inline-block mt-1 text-[10px] font-bold px-1.5 py-0.5 rounded" 
                    [ngClass]="leave.isHalfDay ? 'bg-amber-100 text-amber-800' : 'bg-indigo-100 text-indigo-800'">
                {{ leave.isHalfDay ? 'Half Day' : 'Full Day' }}
              </span>
            </td>
            <td class="p-4">
              <span class="font-semibold text-gray-800">{{ leave.deductedQuotaDays }} days</span>
            </td>
            <td class="p-4 max-w-xs truncate" [title]="leave.reason">{{ leave.reason }}</td>
            <td class="p-4">
              <span [ngClass]="{
                'bg-yellow-100 text-yellow-800': leave.status === 0,
                'bg-green-100 text-green-800': leave.status === 1,
                'bg-red-50 text-red-700': leave.status === 2,
                'bg-red-100 text-red-800 border border-red-200': leave.status === 3
              }" class="px-2.5 py-1 rounded-full text-xs font-semibold uppercase tracking-wider">
                {{ leave.status === 0 ? 'Pending' : leave.status === 1 ? 'Approved' : leave.status === 3 ? 'Unplanned' : 'Rejected' }}
              </span>
            </td>
            <td class="p-4 text-gray-500 italic max-w-xs truncate" [title]="leave.adminComment || ''">
              {{ leave.adminComment || '-' }}
            </td>
            <td class="p-4 text-right">
              <div class="flex justify-end gap-2">
                <button *ngIf="leave.status === 0" (click)="openCommentModal(leave, 'approve')" class="px-3 py-1.5 bg-green-50 text-green-700 hover:bg-green-100 border border-green-200 rounded-lg text-xs font-semibold transition-colors">
                  Approve
                </button>
                <button *ngIf="leave.status === 0 || leave.status === 1 || leave.status === 3" (click)="openCommentModal(leave, 'reject')" class="px-3 py-1.5 bg-red-50 text-red-700 hover:bg-red-100 border border-red-200 rounded-lg text-xs font-semibold transition-colors">
                  Reject
                </button>
              </div>
            </td>
          </tr>
          <tr class="h-16" *ngIf="leaves.length === 0">
            <td colspan="8" class="p-8 text-center text-gray-400 italic bg-gray-50">
              No leave requests found matching filters.
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>

  <!-- Mobile Card View: Styling reference match to appointment-card layout -->
  <div class="block md:hidden space-y-4 mb-6">
    <div *ngFor="let leave of leaves" class="bg-white rounded-xl border border-gray-200 p-5 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200">
      <!-- Card Header -->
      <div class="flex justify-between items-start pb-4 mb-4 border-b border-gray-150">
        <div>
          <h3 class="text-gray-900 font-bold text-base">{{ leave.agentName || 'N/A' }}</h3>
          <div class="text-gray-500 text-xs mt-1">ID: {{ leave.agentId }}</div>
        </div>
        <span [ngClass]="{
          'bg-yellow-100 text-yellow-800': leave.status === 0,
          'bg-green-100 text-green-800': leave.status === 1,
          'bg-red-50 text-red-700': leave.status === 2,
          'bg-red-100 text-red-800 border border-red-200': leave.status === 3
        }" class="px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider">
          {{ leave.status === 0 ? 'Pending' : leave.status === 1 ? 'Approved' : leave.status === 3 ? 'Unplanned' : 'Rejected' }}
        </span>
      </div>

      <!-- Card Content -->
      <div class="space-y-3">
        <div class="flex flex-col sm:flex-row sm:justify-between sm:items-center py-1.5 border-b border-gray-50 last:border-b-0 text-sm">
          <span class="text-gray-500 font-medium">Requested At:</span>
          <span class="text-gray-900 font-semibold mt-0.5 sm:mt-0 sm:text-right">
            {{ leave.createdAt | date:'mediumDate' }} {{ leave.createdAt | date:'shortTime' }}
          </span>
        </div>
        
        <div class="flex flex-col sm:flex-row sm:justify-between sm:items-center py-1.5 border-b border-gray-50 last:border-b-0 text-sm">
          <span class="text-gray-500 font-medium">Leave Date:</span>
          <div class="flex items-center gap-2 mt-0.5 sm:mt-0">
            <span class="text-gray-900 font-bold">{{ leave.date | date:'mediumDate' }}</span>
            <span class="text-[10px] font-bold px-2 py-0.5 rounded" 
                  [ngClass]="leave.isHalfDay ? 'bg-amber-100 text-amber-800' : 'bg-indigo-100 text-indigo-800'">
              {{ leave.isHalfDay ? 'Half Day' : 'Full Day' }}
            </span>
          </div>
        </div>

        <div class="flex flex-col sm:flex-row sm:justify-between sm:items-center py-1.5 border-b border-gray-50 last:border-b-0 text-sm">
          <span class="text-gray-500 font-medium">Quota Deducted:</span>
          <span class="text-gray-900 font-bold mt-0.5 sm:mt-0 sm:text-right">{{ leave.deductedQuotaDays }} days</span>
        </div>

        <div class="flex flex-col gap-1.5 pt-1.5">
          <span class="text-gray-500 font-medium text-sm">Reason:</span>
          <span class="text-gray-800 bg-gray-50 border border-gray-150 rounded-lg p-3 text-xs italic block whitespace-pre-wrap leading-relaxed">
            {{ leave.reason }}
          </span>
        </div>

        <div class="flex flex-col gap-1.5 pt-1.5" *ngIf="leave.adminComment">
          <span class="text-gray-500 font-medium text-sm">Admin Comment:</span>
          <span class="text-red-700 bg-red-50 border border-red-100 rounded-lg p-3 text-xs italic block whitespace-pre-wrap leading-relaxed">
            {{ leave.adminComment }}
          </span>
        </div>
      </div>

      <!-- Card Actions -->
      <div class="flex justify-end gap-2 mt-5 pt-4 border-t border-gray-100" *ngIf="leave.status === 0 || leave.status === 1 || leave.status === 3">
        <button *ngIf="leave.status === 0" (click)="openCommentModal(leave, 'approve')" class="px-4 py-2.5 bg-green-50 text-green-700 hover:bg-green-100 border border-green-200 rounded-lg text-xs font-bold transition-colors flex-1 sm:flex-none">
          Approve
        </button>
        <button (click)="openCommentModal(leave, 'reject')" class="px-4 py-2.5 bg-red-50 text-red-700 hover:bg-red-100 border border-red-200 rounded-lg text-xs font-bold transition-colors flex-1 sm:flex-none">
          Reject
        </button>
      </div>
    </div>

    <!-- Empty State for Mobile -->
    <div class="flex flex-col justify-center items-center py-16 px-4 bg-white rounded-xl border border-gray-200 shadow-sm text-center" *ngIf="leaves.length === 0">
      <i class="fas fa-calendar-times text-gray-300 text-5xl mb-4"></i>
      <h3 class="text-gray-700 text-lg font-bold mb-1">No Leave Requests Found</h3>
      <p class="text-gray-500 text-sm">No leave requests match your current filters.</p>
    </div>
  </div>

  <!-- Pagination: Aligned and formatted -->
  <div class="flex justify-center mt-6" *ngIf="leaves.length > 0">
    <app-pagination [page]="currentPage" [totalPages]="totalPages" [pageSize]="pageSize"
      (pageChange)="onPageChange($event)" (pageSizeChange)="onPageSizeChange($event)">
    </app-pagination>
  </div>

  <!-- Dialog: Approve/Reject Comment Modal with style reference layout -->
  <div *ngIf="showCommentModal" class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in p-4">
    <div class="bg-white rounded-xl shadow-2xl border border-gray-100 max-w-md w-full overflow-hidden transform transition-all duration-300 scale-100">
      <!-- Modal Header with gradient matching the style reference theme -->
      <div class="bg-gradient-to-r from-emerald-600 to-teal-800 text-white p-5 flex justify-between items-center">
        <h3 class="text-lg font-bold">
          {{ commentAction === 'approve' ? 'Approve' : 'Reject' }} Leave Request
        </h3>
        <button (click)="closeCommentModal()" class="text-white/80 hover:text-white bg-white/10 hover:bg-white/20 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200">
          <i class="fas fa-times"></i>
        </button>
      </div>
      
      <!-- Modal Body -->
      <div class="p-6">
        <p class="text-sm text-gray-500 mb-4 leading-relaxed">
          Provide an administrative comment or explanation for this decision. <span class="font-semibold text-rose-600" *ngIf="commentAction === 'reject'">Required for rejection.</span>
        </p>

        <div class="space-y-2">
          <label class="block text-sm font-semibold text-gray-700">Admin Comment</label>
          <textarea [(ngModel)]="adminComment" placeholder="Write comment here..." rows="3" class="w-full border-2 border-gray-150 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-50 rounded-lg p-3 text-sm text-gray-700 focus:outline-none transition-all duration-200 resize-none"></textarea>
        </div>
      </div>

      <!-- Modal Footer -->
      <div class="bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t border-gray-100">
        <button (click)="closeCommentModal()" class="px-5 py-2.5 border-2 border-gray-200 hover:border-gray-300 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-100 transition-colors">
          Cancel
        </button>
        <button (click)="submitCommentAction()" [ngClass]="{
          'bg-emerald-600 hover:bg-emerald-700 shadow-emerald-100 hover:shadow-lg': commentAction === 'approve',
          'bg-rose-600 hover:bg-rose-700 shadow-rose-100 hover:shadow-lg': commentAction === 'reject'
        }" class="px-5 py-2.5 rounded-lg text-sm font-bold text-white shadow-md transition-all duration-200">
          Confirm
        </button>
      </div>
    </div>
  </div>

  <!-- Notification Toast -->
  <div *ngIf="notification.show" [ngClass]="{
    'bg-emerald-600 shadow-emerald-100': notification.type === 'success',
    'bg-rose-600 shadow-rose-100': notification.type === 'error',
    'bg-sky-600 shadow-sky-100': notification.type === 'info'
  }" class="fixed bottom-6 right-6 z-50 text-white px-6 py-4 rounded-xl shadow-2xl flex items-center gap-3 transition-all duration-300 animate-slide-up border border-white/10">
    <i [ngClass]="{
      'fa-check-circle': notification.type === 'success',
      'fa-exclamation-circle': notification.type === 'error',
      'fa-info-circle': notification.type === 'info'
    }" class="fas text-lg"></i>
    <span class="font-semibold text-sm">{{ notification.message }}</span>
  </div>
</div>

